import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Copy, FileDown, FileText, Loader2, Mail, Pencil, Plus, Trash2 } from "lucide-react";
import { amt, emptyInvoice, formFromInvoice, invoicePayload } from "../lib/invoice";
import InvoiceForm from "./InvoiceForm";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicesPanel({ month, allTime, monthLabel }) {
  const { can } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [nextNumber, setNextNumber] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInvoice);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [mailing, setMailing] = useState(null);
  const [mailTo, setMailTo] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [mailState, setMailState] = useState("");

  const load = useCallback(async () => {
    const query = allTime ? "" : `?month=${month}`;
    const [inv, cus, set, next] = await Promise.all([
      axios.get(`${API}/invoices${query}`),
      axios.get(`${API}/invoices/customers`).catch(() => ({ data: [] })),
      axios.get(`${API}/invoices/settings`).catch(() => ({ data: {} })),
      axios.get(`${API}/invoices/next-number`).catch(() => ({ data: { number: "" } })),
    ]);
    setInvoices(inv.data);
    setCustomers(cus.data);
    setSettings(set.data);
    setNextNumber(next.data.number);
  }, [month, allTime]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const openCreate = () => {
    setForm(emptyInvoice(settings));
    setNotice("");
    setCreating(true);
  };

  const openEdit = invoice => {
    setForm(formFromInvoice(invoice));
    setNotice("");
    setEditing(invoice);
  };

  const submit = async event => {
    event.preventDefault();
    const payload = invoicePayload(form);
    if (!payload.items.length) { setNotice("En az bir fatura kalemi girin (açıklama ve tutar)"); return; }
    if (!payload.customer.name.trim()) { setNotice("Müşteri ünvanı gerekli"); return; }
    try {
      if (editing) await axios.put(`${API}/invoices/${editing.id}`, payload);
      else await axios.post(`${API}/invoices`, payload);
      setCreating(false);
      setEditing(null);
      await load();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Fatura kaydedilemedi");
    }
  };

  const download = async invoice => {
    setBusy(invoice.id);
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = `fatura-${invoice.number}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setNotice("PDF oluşturulamadı");
    } finally {
      setBusy("");
    }
  };

  const confirmDelete = async () => {
    await axios.delete(`${API}/invoices/${pendingDelete.id}`);
    setPendingDelete(null);
    await load();
  };

  const duplicate = async invoice => {
    setBusy(invoice.id);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/duplicate`);
      await load();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Fatura kopyalanamadı");
    } finally {
      setBusy("");
    }
  };

  const openMail = invoice => {
    setMailTo(invoice.customer?.email || "");
    setMailMessage("");
    setMailState("");
    setMailing(invoice);
  };

  const sendMail = async event => {
    event.preventDefault();
    setMailState("sending");
    try {
      const { data } = await axios.post(`${API}/invoices/${mailing.id}/email`, { to: mailTo || null, message: mailMessage });
      setMailState(`Gönderildi: ${data.to}`);
    } catch (e) {
      setMailState(e?.response?.data?.detail || "E-posta gönderilemedi");
    }
  };

  const total = invoices.reduce((sum, i) => sum + i.grand_total, 0);
  const totalCurrency = invoices[0]?.total_currency || settings.total_currency || "AED";
  const mixed = invoices.some(i => i.total_currency !== totalCurrency);

  const dialogForm = (prefix, onCancel) => (
    <form className="edit-form" onSubmit={submit} data-testid={`${prefix}-form`}>
      <InvoiceForm form={form} setForm={setForm} customers={customers} prefix={prefix}/>
      <div className="dialog-actions">
        <button type="button" className="outline-btn" data-testid={`${prefix}-cancel`} onClick={onCancel}>Vazgeç</button>
        <button type="submit" className="primary-btn" data-testid={`${prefix}-save`}>Faturayı kaydet</button>
      </div>
      {notice && <div className="notice error" data-testid={`${prefix}-notice`}>{notice}</div>}
    </form>
  );

  return (
    <section className="panel invoices-panel" data-testid="invoices-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">FATURALAR</p>
          <h3>{allTime ? "Tüm faturalar" : `${monthLabel} faturaları`}</h3>
          <small className="hint">Sıradaki fatura no: <b data-testid="next-invoice-number">{nextNumber || "—"}</b></small>
        </div>
        <div className="head-side">
          <div className="collect-total">
            <small>{invoices.length} fatura · toplam</small>
            <strong data-testid="invoices-total">{mixed ? "Karışık para birimi" : amt(total, totalCurrency)}</strong>
          </div>
          {can("create") && (
            <button className="primary-btn" data-testid="new-invoice-button" onClick={openCreate}>
              <Plus size={16}/> Yeni fatura
            </button>
          )}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fatura no</th><th>Tarih</th><th>Müşteri</th><th>Açıklama</th>
              <th className="right">Kalem tutarı</th><th className="right">VAT</th><th className="right">Genel toplam</th><th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                <td><b>{inv.number}</b></td>
                <td>{inv.invoice_date}</td>
                <td data-testid={`invoice-customer-${inv.number}`}><b>{inv.customer?.name}</b></td>
                <td>
                  {inv.items?.[0]?.description}
                  {inv.items?.length > 1 && <small className="sub-line">+{inv.items.length - 1} kalem</small>}
                </td>
                <td className="right amount">{amt(inv.subtotal_src, inv.item_currency)}</td>
                <td className="right amount">{amt(inv.vat_amount, inv.total_currency)}</td>
                <td className="right amount income" data-testid={`invoice-grand-${inv.number}`}>
                  {amt(inv.grand_total, inv.total_currency)}
                  {inv.item_currency !== inv.total_currency && <small className="sub-line">kur {inv.rate.toFixed(4)}</small>}
                </td>
                <td className="row-actions">
                  <button className="icon-btn" title="PDF indir" data-testid={`invoice-pdf-${inv.number}`}
                    disabled={busy === inv.id} onClick={() => download(inv)}>
                    {busy === inv.id ? <Loader2 size={15} className="spin"/> : <FileDown size={15}/>}
                  </button>
                  {can("reports") && (
                    <button className="icon-btn" title="Müşteriye e-posta ile gönder" data-testid={`invoice-email-${inv.number}`}
                      onClick={() => openMail(inv)}><Mail size={15}/></button>
                  )}
                  {can("create") && (
                    <button className="icon-btn" title="Faturayı kopyala" data-testid={`invoice-duplicate-${inv.number}`}
                      disabled={busy === inv.id} onClick={() => duplicate(inv)}><Copy size={15}/></button>
                  )}
                  {can("edit") && (
                    <button className="icon-btn" title="Faturayı düzenle" data-testid={`invoice-edit-${inv.number}`}
                      onClick={() => openEdit(inv)}><Pencil size={15}/></button>
                  )}
                  {can("delete") && (
                    <button className="icon-btn danger" title="Faturayı sil" data-testid={`invoice-delete-${inv.number}`}
                      onClick={() => setPendingDelete(inv)}><Trash2 size={15}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!invoices.length && <div className="empty-table" data-testid="invoices-empty">Bu dönemde fatura yok.</div>}
      </div>

      <Dialog open={creating} onOpenChange={open => !open && setCreating(false)}>
        <DialogContent className="edit-dialog" data-testid="create-invoice-dialog">
          <DialogHeader>
            <DialogTitle><FileText size={16}/> Yeni fatura</DialogTitle>
            <DialogDescription>Fatura numarası boş bırakılırsa sıradaki numara ({nextNumber || "—"}) otomatik atanır.</DialogDescription>
          </DialogHeader>
          {creating && dialogForm("create-invoice", () => setCreating(false))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent className="edit-dialog" data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Faturayı düzenle</DialogTitle>
            <DialogDescription>Numara, tarih, müşteri, kalemler, kur ve VAT bilgilerini güncelleyebilirsiniz.</DialogDescription>
          </DialogHeader>
          {editing && dialogForm("edit-invoice", () => setEditing(null))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!mailing} onOpenChange={open => !open && setMailing(null)}>
        <DialogContent data-testid="email-invoice-dialog">
          <DialogHeader>
            <DialogTitle><Mail size={16}/> Faturayı e-posta ile gönder</DialogTitle>
            <DialogDescription>
              {mailing && <>{mailing.number} — {mailing.customer?.name} ({amt(mailing.grand_total, mailing.total_currency)}). PDF ek olarak gönderilir.</>}
            </DialogDescription>
          </DialogHeader>
          <form className="edit-form" onSubmit={sendMail}>
            <div className="field">
              <label>Alıcı e-posta</label>
              <input required type="email" data-testid="email-invoice-to" value={mailTo} onChange={e => setMailTo(e.target.value)}/>
            </div>
            <div className="field">
              <label>Ek mesaj <small className="hint">(isteğe bağlı)</small></label>
              <textarea rows={3} data-testid="email-invoice-message" value={mailMessage} onChange={e => setMailMessage(e.target.value)}/>
            </div>
            <div className="dialog-actions">
              <button type="button" className="outline-btn" data-testid="email-invoice-cancel" onClick={() => setMailing(null)}>Kapat</button>
              <button type="submit" className="primary-btn" data-testid="email-invoice-send" disabled={mailState === "sending"}>
                {mailState === "sending" ? <Loader2 size={15} className="spin"/> : <Mail size={15}/>} Gönder
              </button>
            </div>
            {mailState && mailState !== "sending" && <small className="group-notice" data-testid="email-invoice-state">{mailState}</small>}
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-invoice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fatura silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && <><b>{pendingDelete.number}</b> — {pendingDelete.customer?.name} ({amt(pendingDelete.grand_total, pendingDelete.total_currency)}). Günlüğe kaydedilir.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-invoice-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="danger-action" data-testid="delete-invoice-confirm" onClick={confirmDelete}>Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
